/**
 * notesAndFiles preset — the original Cockpit Data Vault use case.
 *
 * Schema type: 'entries'
 *   The AI splits the transcript into 1..N discrete "notes" and assigns each
 *   to one of the account's categories (default taxonomy + custom). It may
 *   also propose brand-new categories when material clearly does not fit an
 *   existing bucket.
 *
 * The caller (CockpitDataVault) provides a `context.categories` array
 *   [{ id, name, hint?, custom? }, ...]
 * so the model sees every category the user actually has, not just the
 * built-in defaults.
 *
 * Preview: entries list with checkboxes + newCategories banner (same UX as
 * the standalone VoiceMemoModal had — this preset just moves it into the
 * generic engine).
 */

import React, { useMemo, useState } from 'react';

// Mirror of the vault's built-in categories. When the caller doesn't pass a
// `context.categories`, we fall back to this so a bare `openCapture` still
// works end-to-end.
const DEFAULT_CATEGORIES = [
  { id: 'header-notes', name: 'Header notes', hint: 'Shows as a note icon in the top navigation. Short intro text + bullet points.' },
  { id: 'business', name: 'My Business', hint: 'What you do, your model, your story.' },
  { id: 'selling-points', name: 'Selling Points', hint: 'USPs and why you\'re different.' },
  { id: 'offer', name: 'No Brainer Offer', hint: 'The irresistible core offer.' },
  { id: 'lead-magnet', name: 'Lead Magnet', hint: 'Free value that attracts leads.' },
  { id: 'proof', name: 'Proof & Testimonials', hint: 'Wins, numbers, social proof.' },
  { id: 'audience', name: 'Target Audience', hint: 'Who you sell to – goals and language.' },
  { id: 'painpoints', name: 'Target Painpoints', hint: 'The problems you solve for them.' },
  { id: 'cta', name: 'Call To Action', hint: 'What you want them to do next.' },
  { id: 'general', name: 'General', hint: 'Anything else worth keeping.' },
];

/**
 * Preview: user picks which proposed entries to keep, sees any new custom
 * categories that will be created, then hits Save. Confirming passes the
 * (filtered) selection to `onConfirm` — the caller persists them.
 */
function EntriesPreview({ structured, context, onConfirm, onCancel, saving }) {
  const entries = structured?.entries || [];
  const newCats = structured?.newCategories || [];
  const [selected, setSelected] = useState(() => {
    const init = {};
    entries.forEach((_, i) => { init[i] = true; });
    return init;
  });

  const catLookup = useMemo(() => {
    const providedCats = context?.categories?.length ? context.categories : DEFAULT_CATEGORIES;
    const map = new Map();
    providedCats.forEach((c) => map.set(c.id, c));
    newCats.forEach((c) => map.set(c.tempId, { id: c.tempId, name: c.name, isNew: true }));
    return map;
  }, [context, newCats]);

  const selectedCount = entries.filter((_, i) => selected[i]).length;
  const allSelected = selectedCount === entries.length;

  const handleToggle = (i) => setSelected((prev) => ({ ...prev, [i]: !prev[i] }));
  const handleToggleAll = (v) => {
    const next = {};
    entries.forEach((_, i) => { next[i] = v; });
    setSelected(next);
  };

  const handleConfirm = () => {
    // Only pass through selected entries and (transitively) only the new
    // categories that at least one selected entry references. Keeps the
    // caller's persistence step small.
    const selectedEntries = entries.filter((_, i) => selected[i]);
    const usedTempIds = new Set(selectedEntries.map((e) => e.categoryId).filter((c) => newCats.some((nc) => nc.tempId === c)));
    const usedNewCats = newCats.filter((c) => usedTempIds.has(c.tempId));
    onConfirm({ entries: selectedEntries, newCategories: usedNewCats, summary: structured.summary || '' });
  };

  return (
    <div className="speakEngine__body speakEngine__body--preview">
      <div className="speakEngine__previewHead">
        <div>
          <h3>{entries.length} {entries.length === 1 ? 'entry' : 'entries'} proposed</h3>
          {structured?.summary && <p className="speakEngine__previewSummary">{structured.summary}</p>}
        </div>
        <button type="button" className="speakEngine__linkBtn" onClick={() => handleToggleAll(!allSelected)}>
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>

      {newCats.length > 0 && (
        <div className="speakEngine__newCatsBanner">
          <span className="speakEngine__newCatsLabel">New categories will be added:</span>
          <div className="speakEngine__newCatsChips">
            {newCats.map((c) => (
              <span key={c.tempId} className="speakEngine__newCatChip">{c.name}</span>
            ))}
          </div>
        </div>
      )}

      <ul className="speakEngine__previewList">
        {entries.map((entry, i) => {
          const cat = catLookup.get(entry.categoryId);
          const isSel = !!selected[i];
          return (
            <li key={i} className={`speakEngine__previewItem ${isSel ? 'is-selected' : 'is-deselected'}`}>
              <button
                type="button"
                className={`speakEngine__previewCheck ${isSel ? 'is-on' : ''}`}
                onClick={() => handleToggle(i)}
                aria-pressed={isSel}
                aria-label={isSel ? 'Deselect entry' : 'Select entry'}
              >
                {isSel && (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </button>
              <div className="speakEngine__previewMain">
                <div className="speakEngine__previewTop">
                  <span className="speakEngine__previewTitle">{entry.title}</span>
                  {cat && (
                    <span className={`speakEngine__previewCat ${cat.isNew ? 'is-new' : ''}`}>
                      {cat.name}
                      {cat.isNew && <span className="speakEngine__previewCatNew">new</span>}
                    </span>
                  )}
                </div>
                <div className="speakEngine__previewSnippet">
                  {entry.content?.length > 320 ? `${entry.content.slice(0, 320).trim()}…` : entry.content}
                </div>
                {entry.tags?.length > 0 && (
                  <div className="speakEngine__previewTags">
                    {entry.tags.map((t) => (
                      <span key={t} className="speakEngine__previewTag">#{t}</span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="speakEngine__previewActions">
        <button type="button" className="speakEngine__btn speakEngine__btn--ghost" onClick={onCancel} disabled={saving}>
          Back to transcript
        </button>
        <button
          type="button"
          className="speakEngine__btn speakEngine__btn--primary"
          onClick={handleConfirm}
          disabled={saving || selectedCount === 0}
        >
          {saving ? (<><span className="speakEngine__btnSpinner" aria-hidden /> Saving…</>)
            : <>Save {selectedCount} {selectedCount === 1 ? 'entry' : 'entries'} to brain</>}
        </button>
      </div>
    </div>
  );
}

const notesAndFilesPreset = {
  id: 'notesAndFiles',
  title: 'Voice memo',
  subtitle: 'AI organises what you say into Notes & Files.',
  intro: {
    heading: 'Tap to start speaking',
    hint: 'Talk for a few minutes. Cover what feels relevant. The AI sorts it into your notes.',
    questionsHeading: 'Guiding questions',
    questions: [
      'What do you do, and how would you explain the business in one sentence?',
      'Who do you sell to, and what problems do they actually have?',
      'What is the offer, and why is it an easy yes?',
      'What makes you different from everyone else?',
      'Any proof, numbers, or customer wins worth keeping?',
      'What should they do next, and do you have something free they can take?',
    ],
  },
  organizeCta: 'Organise with AI',
  organizingText: 'AI is grouping everything into the right categories, cleaning up wording, and splitting distinct topics into separate entries. Usually 3–8 seconds.',
  doneText: (n) => `${n} ${n === 1 ? 'entry' : 'entries'} added to your brain`,
  // Schema sent to the backend structuring function.
  schema: {
    type: 'entries',
    categoriesFromContext: 'categories', // where in `context` to find category list
  },
  // Render the preview UI for this preset.
  PreviewComponent: EntriesPreview,
};

export default notesAndFilesPreset;
